import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getToken,
  setToken,
  clearToken,
  getMe,
  login as apiLogin,
  register as apiRegister,
} from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, if a token exists, hydrate the user from /auth/me.
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      // Ensure axios defaults carry the token before the first call.
      setToken(token);
      try {
        const me = await getMe();
        if (!cancelled) setUser(me);
      } catch (err) {
        clearToken();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  // The login/register responses carry only a bare {id, email} user (no
  // subscription), so after storing the token we hydrate the authoritative,
  // fully-shaped user (with subscription) via /auth/me. This keeps the user
  // object identical across every code path and prevents a paying user from
  // being bounced to /subscribe immediately after logging in.
  const login = useCallback(async (email, password) => {
    const { token } = await apiLogin(email, password);
    setToken(token);
    const me = await getMe();
    setUser(me);
    return me;
  }, []);

  const register = useCallback(async (email, password) => {
    const { token } = await apiRegister(email, password);
    setToken(token);
    const me = await getMe();
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  // Re-fetch the current user (e.g. after a subscription change).
  const refresh = useCallback(async () => {
    if (!getToken()) return null;
    try {
      const me = await getMe();
      setUser(me);
      return me;
    } catch (err) {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export default AuthContext;
